import { inject, Injectable } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import {RecordTableColumnParams} from "../../types/records-table.types";
import {UserPreferencesService} from "../user-preferences.service";
import {ResizeParams, UserPreferences} from "../../types/user-preferences.types";

@Injectable()
export class ColumnResizeService {
  private userPreferencesService = inject(UserPreferencesService);

  private resizeSubscriptions = new Subscription();
  private initialX = 0;
  private initialWidth = 0;
  private columnElement: HTMLElement | null = null;
  private columnKey = '';
  private currentColumnParam: RecordTableColumnParams | null = null;
  private columnElements = new Map<string, HTMLElement[]>();
  private isFrameScheduled = false;
  private lastWidth = 0;
  private tableId: string = '';

  public setTableId(tableId: string): void {
    this.tableId = tableId;
  }

  public registerElement(columnKey: string, element: HTMLElement): void {
    if (!this.columnElements.has(columnKey)) { this.columnElements.set(columnKey, []); }
    this.columnElements.get(columnKey)?.push(element);
  }

  public clearAllRegisteredElements(): void {
    this.columnElements.clear();
  }

  public handleResizeStart(event: MouseEvent, columnParam: RecordTableColumnParams): void {
    event.preventDefault();
    event.stopPropagation();

    const columnElement = (event.currentTarget as HTMLElement).closest('.header-cell') as HTMLElement;
    if (!columnElement) { return; }

    this.columnElement = columnElement;
    this.initialX = event.clientX;
    this.initialWidth = columnElement.offsetWidth;
    this.columnKey = columnParam.key;
    this.currentColumnParam = columnParam;
    this.columnElement.classList.add('resizing');
    this.resizeSubscriptions.unsubscribe();
    this.resizeSubscriptions = new Subscription();
    this.setupMouseMove();
    this.setupMouseUp();
  }

  private setupMouseMove(): void {
    const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove').subscribe(event => this.handleMouseMove(event));
    this.resizeSubscriptions.add(mouseMove$);
  }

  private setupMouseUp(): void {
    const mouseUp$ = fromEvent(document, 'mouseup').subscribe(() => this.handleResizeEnd());
    this.resizeSubscriptions.add(mouseUp$);
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.columnElement || !this.currentColumnParam) { return; }

    const minWidth = this.currentColumnParam.type === 'MODAL' ?
      this.calculateMaxContentWidthAlternative(this.currentColumnParam.key) :
      50;

    const deltaX = event.clientX - this.initialX;
    this.lastWidth = Math.max(minWidth, this.initialWidth + deltaX);

    if (!this.isFrameScheduled) {
      this.isFrameScheduled = true;
      requestAnimationFrame(() => {
        this.updateColumnWidthInTable(this.lastWidth);
        this.isFrameScheduled = false;
      });
    }
  }

  private calculateMaxContentWidthAlternative(dataColumnValue: string): number {
    const selector = `[data-column="${dataColumnValue}"].body-cell .table-btn-group`;
    const buttonGroups = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;

    return Array.from(buttonGroups).reduce((maxWidth, buttonGroup) => {
      return Math.max(maxWidth, buttonGroup.offsetWidth + 10);
    }, 50);
  }

  private updateColumnWidthInTable(newWidth: number): void {
    if (!this.columnKey) { return; }

    const widthPx = `${newWidth}px`;
    const elements = this.columnElements.get(this.columnKey);
    if (elements) {
      for (let el of elements) {
        el.style.minWidth = widthPx;
        el.style.maxWidth = widthPx;
      }
    }

    if (this.columnElement) {
      this.columnElement.style.minWidth = widthPx;
      this.columnElement.style.maxWidth = widthPx;
    }
  }

  private handleResizeEnd(): void {
    if (!this.columnElement) { return; }

    this.columnElement.classList.remove('resizing');
    const finalWidth = this.columnElement.offsetWidth;
    this.saveResizeParams(finalWidth);
    this.cleanup();
  }

  private saveResizeParams(newWidth: number): void {
    const resizeParams = this.getResizeParams();
    const compositeKey = `${this.tableId}_${this.columnKey}`;
    const existingIndex = resizeParams.findIndex(item => item.key === compositeKey);

    if (existingIndex !== -1) {
      resizeParams[existingIndex] = { ...resizeParams[existingIndex], width: newWidth };
    } else {
      resizeParams.push({ key: compositeKey, width: newWidth });
    }

    this.userPreferencesService.saveUserPreferences({ resizeParams });
  }

  private getResizeParams(): Array<ResizeParams> {
    const prefs: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    return prefs?.resizeParams ?? [];
  }

  private cleanup(): void {
    this.resizeSubscriptions.unsubscribe();
    this.columnElement = null;
    this.columnKey = '';
    this.currentColumnParam = null;
    this.isFrameScheduled = false;
  }
}
